/**
 * The score row's width arithmetic, in ONE place (ISSUE-908 R7, Scores UX spec
 * v1.0 section 1.4). Shared by web and mobile.
 *
 * `packages/theme/src/tokens.test.ts`, the web `ScoreRow`'s first render
 * estimate and `tests/e2e/src/app/scoreRowGeometry.e2e.ts` all answer the same
 * question, "how much of this row reaches the team name", from here. Change a
 * number here or nowhere.
 *
 * The anatomy, left to right, on a phone:
 *
 *   card border 1 | inset 12 | mark 28 | gap 8 | NAME | gap 8 | score 36 |
 *   inset 12 | hairline 1 | inset 12 | status 88 | inset 12 | card border 1
 *
 * At 640px and up the status column is 152 and, on web, the bell column (one
 * touch target) follows it. A row with no score (scheduled, postponed,
 * canceled) has no score cell and no gap before it, and the name takes both.
 *
 * Pure: no DOM, no Intl, no React. Every term is a pixel token, so the answer
 * is the same at every text scale.
 */
import { tokens } from "../tokens";

/** Three digits of `text.score` (20px, 800) in tabular figures, rounded UP.
 *  The one term in this file that is a measurement rather than a token. */
export const SCORE_ROW_SCORE_CELL_PX = 36;

/** The width at and above which the status column widens to
 *  `scoreStatusWidth` and the web bell column appears. Tailwind's `sm`. */
export const SCORE_ROW_WIDE_MIN_VIEWPORT_PX = 640;

/** The bell column after the status column at 640px and up (ISSUE-979). Web
 *  only; mobile may adopt it. One touch target wide. */
export const SCORE_ROW_BELL_COLUMN_PX = tokens.size.touchTarget;

/** The inline favorite star after a followed team's name: a 16px glyph and
 *  its 4px gap (section 1.6). */
export const SCORE_ROW_FAVORITE_STAR_PX =
  tokens.size.iconSm + tokens.size.scoreLiveGap;

/** The inline possession dot after a name while in play: 8 plus its 4 gap. */
export const SCORE_ROW_POSSESSION_DOT_PX =
  tokens.size.scoreLiveDot + tokens.size.scoreLiveGap;

/** ISSUE-1393. The inline "PP" badge after a hockey team's name while it is
 *  on a power play: an `iconLg` wide badge plus its 4 gap. */
export const SCORE_ROW_POWER_PLAY_BADGE_PX =
  tokens.size.iconLg + tokens.size.scoreLiveGap;

/** One page gutter, both sides. A score list sits inside exactly ONE. */
export function scoreRowPageGutterPx(): number {
  return tokens.size.pageGutter * 2;
}

/** The section card at a layout viewport: the page column (capped at
 *  `contentWidth`), minus one page gutter each side and the card border. */
export function scoreRowCardWidthPx(layoutViewportPx: number): number {
  return (
    Math.min(layoutViewportPx, tokens.size.contentWidth) -
    scoreRowPageGutterPx() -
    tokens.size.hairline * 2
  );
}

export type typeScoreRowShape = {
  /** False for scheduled, postponed and canceled rows: no score cell. */
  hasScore: boolean;
  /** True at 640px and up: the 152 status column. */
  isWide: boolean;
  /** True when the bell column follows the status column (web, wide only). */
  hasBell: boolean;
};

/** Everything right of the teams block: the teams block's own end padding,
 *  the hairline, the status column's start padding and the status label box,
 *  plus the bell column when present. 113 on a phone. */
export function scoreRowStatusZonePx(
  shape: Pick<typeScoreRowShape, "isWide" | "hasBell">,
): number {
  const statusBox = shape.isWide
    ? tokens.size.scoreStatusWidth
    : tokens.size.scoreStatusCompactWidth;
  return (
    tokens.size.scoreRowInset +
    tokens.size.hairline +
    tokens.size.scoreRowInset +
    statusBox +
    (shape.hasBell ? SCORE_ROW_BELL_COLUMN_PX : 0)
  );
}

/** The name cell inside a row of this outer width (the card's inner width). */
export function scoreRowNameCellInRowPx(
  rowWidthPx: number,
  shape: typeScoreRowShape,
): number {
  const teamsBlock =
    rowWidthPx - tokens.size.scoreRowInset * 2 - scoreRowStatusZonePx(shape);
  const scoreTerm = shape.hasScore
    ? tokens.size.scoreRowGap + SCORE_ROW_SCORE_CELL_PX
    : 0;
  return (
    teamsBlock - tokens.size.scoreMarkSize - tokens.size.scoreRowGap - scoreTerm
  );
}

/**
 * The name cell of a row in a single column card at a phone layout viewport
 * (under 640). 124 at 375 and 139 at 390 with a score cell, 168 and 183
 * without. `tokens.test.ts` asserts these; nothing restates them in prose.
 */
export function scoreRowNameCellPx(
  layoutViewportPx: number,
  options: { hasScore: boolean } = { hasScore: true },
): number {
  return scoreRowNameCellInRowPx(scoreRowCardWidthPx(layoutViewportPx), {
    hasScore: options.hasScore,
    isWide: false,
    hasBell: false,
  });
}

/**
 * The first render estimate for a row before its name cell is measured, so a
 * label does not flash from long to short. Phone: the single card. Wide: a
 * single card with the 152 status column and, on web, the bell. From `lg` the
 * web home lays cards out two across, which `columns` describes.
 */
export function scoreRowNameCellEstimatePx(input: {
  layoutViewportPx: number;
  hasScore: boolean;
  hasBellWhenWide: boolean;
  columns?: 1 | 2;
}): number {
  const isWide = input.layoutViewportPx >= SCORE_ROW_WIDE_MIN_VIEWPORT_PX;
  const columns = input.columns ?? 1;
  const column =
    (Math.min(input.layoutViewportPx, tokens.size.contentWidth) -
      scoreRowPageGutterPx() -
      (columns - 1) * tokens.space.lg) /
    columns;
  return scoreRowNameCellInRowPx(column - tokens.size.hairline * 2, {
    hasScore: input.hasScore,
    isWide,
    hasBell: isWide && input.hasBellWhenWide,
  });
}
